import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from '../components/Toast';

const NotificationContext = createContext(null);

let toastId = 0;

export function NotificationProvider({ children }) {
  const [state, setState] = useState({
    open: false,
    type: 'alert', // 'alert' | 'confirm'
    message: '',
    onConfirm: null,
  });
  const [toasts, setToasts] = useState([]);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false, message: '', onConfirm: null }));
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type, message, duration) => {
    const id = ++toastId;
    const entry = { id, type, message: String(message), duration };
    setToasts((prev) => [...prev, entry]);
    return id;
  }, []);

  const toast = useMemo(
    () => ({
      success: (message, duration = 5000) => addToast('success', message, duration),
      error: (message, duration = 7000) => addToast('error', message, duration),
      warning: (message, duration = 7000) => addToast('warning', message, duration),
      info: (message, duration = 5000) => addToast('info', message, duration),
    }),
    [addToast]
  );

  const showAlert = useCallback((message) => {
    setState({ open: true, type: 'alert', message: String(message), onConfirm: null });
  }, []);

  const showConfirm = useCallback((message, onConfirm) => {
    setState({
      open: true,
      type: 'confirm',
      message: String(message),
      onConfirm: typeof onConfirm === 'function' ? onConfirm : null,
    });
  }, []);

  return (
    <NotificationContext.Provider value={{ showAlert, showConfirm, close, toast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
      <NotificationModal
        open={state.open}
        type={state.type}
        message={state.message}
        onConfirm={state.onConfirm}
        onClose={close}
      />
    </NotificationContext.Provider>
  );
}

function NotificationModal({ open, type, message, onConfirm, onClose }) {
  const { t } = useTranslation();
  if (!open) return null;

  const handleConfirm = () => {
    if (type === 'confirm' && onConfirm) onConfirm();
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notification-message"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(0,0,0,0.75)',
        animation: 'fadeIn 0.2s ease',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          maxWidth: '400px',
          width: '100%',
          padding: '1.5rem',
          boxShadow: 'var(--shadow-card), 0 0 0 1px var(--border)',
          animation: 'fadeInUp 0.25s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <p
          id="notification-message"
          style={{
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-body)',
            fontSize: '0.95rem',
            lineHeight: 1.5,
            marginBottom: '1.25rem',
          }}
        >
          {message}
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          {type === 'confirm' && (
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                fontFamily: 'var(--font-body)',
                fontSize: '0.9rem',
                color: 'var(--text-muted)',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              {t('common.cancel')}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              padding: '0.5rem 1.25rem',
              fontFamily: 'var(--font-body)',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#fff',
              background: 'var(--orange)',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            {t('common.ok')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    const noop = () => {};
    return {
      showAlert: (msg) => window.alert(msg),
      showConfirm: (msg, fn) => window.confirm(msg) && fn?.(),
      close: noop,
      toast: {
        success: noop,
        error: noop,
        warning: noop,
        info: noop,
      },
    };
  }
  return ctx;
}
